import { useEffect, useState } from "react";
import { Loader2, UserPlus, UserRound, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";

/**
 * Reusable Add-cast-member dialog used by CastHub and OnSetMode.
 *
 * Fields mirror CastHub's original inline dialog — Number / Name (required)
 * / Actor / Main-Crowd toggle defaulting to Crowd. Defaults reset on every
 * `open` transition so each visit starts blank. The parent receives the
 * created character through `onCreated(data)` and is responsible for
 * inserting it into its own list state (lets the parent decide ordering /
 * filtering).
 */
export default function CreateCharacterDialog({ open, projectId, onClose, onCreated }) {
    const apiBase = useApiBase();
    const [name, setName] = useState("");
    const [actor, setActor] = useState("");
    const [number, setNumber] = useState("");
    const [team, setTeam] = useState("crowd");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        // Reset state every time the dialog opens. Typing into a fresh
        // form is faster than clearing leftover values.
        setName("");
        setActor("");
        setNumber("");
        setTeam("crowd");
        setError("");
    }, [open]);

    const close = () => {
        if (submitting) return;
        onClose?.();
    };

    const submit = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError("Name cannot be blank.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.post(`${apiBase}/${projectId}/characters`, {
                name: trimmed,
                actor: actor.trim() || null,
                character_number: number.trim() || null,
                team,
            });
            toast.success(`Added ${data.name}`);
            onCreated?.(data);
            onClose?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && close()}>
            <DialogContent
                data-testid="cast-create-dialog"
                className="max-w-sm w-full bg-card border-white/10 p-5"
            >
                <DialogHeader className="space-y-1 pr-8">
                    <DialogTitle className="font-display text-lg font-bold text-white">
                        Add cast member
                    </DialogTitle>
                    <DialogDescription className="text-xs text-neutral-400">
                        Use this to add Crowd entries or any character we missed
                        when building the cast.
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-4 space-y-3">
                    {error ? (
                        <p
                            data-testid="cast-create-error"
                            className="text-sm text-red-400"
                        >
                            {error}
                        </p>
                    ) : null}
                    <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-1">
                            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                Number
                            </Label>
                            <Input
                                data-testid="cast-create-number-input"
                                value={number}
                                onChange={(e) => setNumber(e.target.value)}
                                placeholder="258"
                                maxLength={40}
                                className="mt-1.5 h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                Name · required
                            </Label>
                            <Input
                                data-testid="cast-create-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Background Diner #3"
                                maxLength={80}
                                autoFocus
                                className="mt-1.5 h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Actor · optional
                        </Label>
                        <Input
                            data-testid="cast-create-actor-input"
                            value={actor}
                            onChange={(e) => setActor(e.target.value)}
                            placeholder="Actor / performer"
                            maxLength={80}
                            className="mt-1.5 h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Team
                        </Label>
                        <div
                            data-testid="cast-create-team-toggle"
                            className="mt-1.5 grid grid-cols-2 p-1 bg-secondary rounded-lg gap-1"
                        >
                            {[
                                { k: "main", label: "Main", icon: UserRound },
                                { k: "crowd", label: "Crowd", icon: Users },
                            ].map(({ k, label, icon: Icon }) => (
                                <button
                                    key={k}
                                    type="button"
                                    data-testid={`cast-create-team-${k}`}
                                    onClick={() => setTeam(k)}
                                    aria-pressed={team === k}
                                    className={`tap h-10 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                                        team === k
                                            ? "bg-primary text-primary-foreground shadow-sm"
                                            : "text-neutral-300 hover:text-white"
                                    }`}
                                >
                                    <Icon size={14} />
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <Button
                        type="button"
                        data-testid="cast-create-cancel"
                        variant="secondary"
                        onClick={close}
                        disabled={submitting}
                        className="min-tap"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        data-testid="cast-create-submit"
                        onClick={submit}
                        disabled={submitting || !name.trim()}
                        className="min-tap font-semibold"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={14} className="mr-2 animate-spin" />
                                Adding…
                            </>
                        ) : (
                            <>
                                <UserPlus size={14} className="mr-2" />
                                Add to cast
                            </>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
